import { useState } from 'react';
import { HeroStatTile } from '../components/HeroStatTile';
import { SkelBar, SkelBadge, SkelAvatar, SkeletonPreviewLabel } from '../components/Skeleton';

/**
 * Signal Assigner visual shell. Admin-only. Ports v0.7 CoverageReportPage
 * (index.html ~5665).
 *
 * 7.1d update: Owner/member row list shell replaces card grid. Layout
 * mirrors v0.7 (line ~5740 partitioning):
 *   - SMD section (Senior Managing Directors with contacts)
 *   - MD section (Managing Directors with contacts)
 *   - No-contacts section (members without contacts yet)
 *
 * Each row shows:
 *   - Avatar + name + role
 *   - Assigned / Matched / Pending count chips
 *   - Score threshold badge
 *   - Sort menu (disabled)
 *   - Test/Assign action buttons (disabled)
 */
export function SignalAssigner() {
  const [search, setSearch] = useState('');
  const [scoreThresh, setScoreThresh] = useState(65);

  const smdRows = [
    { nameW: 132, roleW: 168 },
    { nameW: 108, roleW: 152 },
    { nameW: 156, roleW: 184 },
  ];
  const mdRows = [
    { nameW: 124, roleW: 144 },
    { nameW: 96, roleW: 132 },
    { nameW: 142, roleW: 156 },
    { nameW: 116, roleW: 148 },
  ];
  const noContactRows = [{ nameW: 104, roleW: 128 }, { nameW: 132, roleW: 144 }];

  return (
    <div>
      <div className="bdse-hero">
        <div className="bdse-hero-row">
          <div>
            <h1 className="bdse-hero-title">Signal Assigner</h1>
            <p className="bdse-hero-subtitle">
              Per-owner coverage and pursuit performance
            </p>
          </div>
          <div className="bdse-hero-actions">
            <button
              type="button"
              disabled
              title="Coming in Stream 3D"
              className="bdse-hero-btn-ghost"
            >
              Sort ▾
            </button>
            <button
              type="button"
              disabled
              title="Coming in Stream 3D"
              className="bdse-hero-btn-primary"
            >
              Export Coverage
            </button>
          </div>
        </div>

        <div className="bdse-hero-stats">
          <HeroStatTile label="Owners" value="0" sub="active" icon="users" />
          <HeroStatTile label="Assigned" value="0" sub="signals" icon="briefcase" />
          <HeroStatTile label="Stale" value="0" sub="over threshold" icon="clock" />
          <HeroStatTile label="Unclaimed" value="0" sub="awaiting" icon="inbox" />
          <HeroStatTile label="Coverage" value="0%" sub="of high-score" icon="pie" />
        </div>
      </div>

      <div className="bdse-assigner-controls">
        <div className="bdse-search-row">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search owners or signals..."
            className="bdse-search-input"
            aria-label="Search owners"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="bdse-search-clear"
              aria-label="Clear search"
            >
              ×
            </button>
          )}
        </div>
        <div className="bdse-assigner-thresh">
          <label className="bdse-filter-label" htmlFor="score-thresh">
            Min score
          </label>
          <input
            id="score-thresh"
            type="range"
            min={50}
            max={100}
            value={scoreThresh}
            onChange={(e) => setScoreThresh(Number(e.target.value))}
            className="bdse-assigner-thresh-input"
          />
          <span className="bdse-assigner-thresh-value">{scoreThresh}</span>
        </div>
      </div>

      <SkeletonPreviewLabel text="Sample preview - owner coverage populates after roster + contacts ingest" />

      <section className="bdse-assigner-section">
        <h3 className="bdse-assigner-section-title">
          Senior Managing Directors{' '}
          <span className="bdse-assigner-section-count">({smdRows.length})</span>
        </h3>
        <div className="bdse-assigner-list">
          {smdRows.map((r, i) => (
            <article key={i} className="bdse-assigner-row bdse-skeleton-card">
              <SkelAvatar size={36} />
              <div className="bdse-assigner-row-id">
                <SkelBar w={r.nameW} h={11} />
                <SkelBar w={r.roleW} h={9} style={{ marginTop: 4 }} />
              </div>
              <div className="bdse-assigner-row-counts">
                <span className="bdse-assigner-count">
                  <span className="bdse-assigner-count-value">--</span>
                  <span className="bdse-assigner-count-label">Assigned</span>
                </span>
                <span className="bdse-assigner-count">
                  <span className="bdse-assigner-count-value">--</span>
                  <span className="bdse-assigner-count-label">Matched</span>
                </span>
                <span className="bdse-assigner-count">
                  <span className="bdse-assigner-count-value">--</span>
                  <span className="bdse-assigner-count-label">Pending</span>
                </span>
              </div>
              <SkelBadge w={64} h={20} />
              <div className="bdse-assigner-row-actions">
                <button
                  type="button"
                  disabled
                  title="Coming in Stream 3D"
                  className="bdse-btn-secondary-small"
                >
                  Test
                </button>
                <button
                  type="button"
                  disabled
                  title="Coming in Stream 3D"
                  className="bdse-btn-secondary-small"
                >
                  Assign
                </button>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="bdse-assigner-section">
        <h3 className="bdse-assigner-section-title">
          Managing Directors{' '}
          <span className="bdse-assigner-section-count">({mdRows.length})</span>
        </h3>
        <div className="bdse-assigner-list">
          {mdRows.map((r, i) => (
            <article key={i} className="bdse-assigner-row bdse-skeleton-card">
              <SkelAvatar size={36} />
              <div className="bdse-assigner-row-id">
                <SkelBar w={r.nameW} h={11} />
                <SkelBar w={r.roleW} h={9} style={{ marginTop: 4 }} />
              </div>
              <div className="bdse-assigner-row-counts">
                <span className="bdse-assigner-count">
                  <span className="bdse-assigner-count-value">--</span>
                  <span className="bdse-assigner-count-label">Assigned</span>
                </span>
                <span className="bdse-assigner-count">
                  <span className="bdse-assigner-count-value">--</span>
                  <span className="bdse-assigner-count-label">Matched</span>
                </span>
                <span className="bdse-assigner-count">
                  <span className="bdse-assigner-count-value">--</span>
                  <span className="bdse-assigner-count-label">Pending</span>
                </span>
              </div>
              <SkelBadge w={64} h={20} />
              <div className="bdse-assigner-row-actions">
                <button
                  type="button"
                  disabled
                  title="Coming in Stream 3D"
                  className="bdse-btn-secondary-small"
                >
                  Test
                </button>
                <button
                  type="button"
                  disabled
                  title="Coming in Stream 3D"
                  className="bdse-btn-secondary-small"
                >
                  Assign
                </button>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="bdse-assigner-section">
        <h3 className="bdse-assigner-section-title">
          No Contacts Yet{' '}
          <span className="bdse-assigner-section-count">({noContactRows.length})</span>
        </h3>
        <p className="bdse-assigner-section-sub">
          Members without contacts cannot be matched to signals. Add contacts
          on the Contacts page.
        </p>
        <div className="bdse-assigner-list">
          {noContactRows.map((r, i) => (
            <article
              key={i}
              className="bdse-assigner-row bdse-skeleton-card bdse-assigner-row-dim"
            >
              <SkelAvatar size={36} />
              <div className="bdse-assigner-row-id">
                <SkelBar w={r.nameW} h={11} />
                <SkelBar w={r.roleW} h={9} style={{ marginTop: 4 }} />
              </div>
              <div className="bdse-assigner-row-empty">No contacts</div>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}
